import { Injectable, inject } from "@angular/core";
import { StorageService } from "../storage-service/storage.service";
import { AssessmentAssetModel } from "src/app/store/models/assesment.model";

interface AssetCompletionRecord {
  assessmentId: string;
  assetId: string;
  isAssessmentCompleted: boolean;
  noFinding?: boolean;
  completedAt: number;
}

@Injectable({
  providedIn: "root",
})
export class OfflineAssessmentCompletionService {
  private storageService = inject(StorageService);
  private storageKey = "assetCompleted";

  async markCompleted(assessmentId: string, assetId: string): Promise<void> {
    await this.upsertRecord(assessmentId, assetId, {
      isAssessmentCompleted: true,
      noFinding: false,
    });
  }

  async markNoFinding(assessmentId: string, assetId: string): Promise<void> {
    await this.upsertRecord(assessmentId, assetId, {
      isAssessmentCompleted: false,
      noFinding: true,
    });
  }

  private async upsertRecord(
    assessmentId: string,
    assetId: string,
    fields: Pick<AssetCompletionRecord, "isAssessmentCompleted" | "noFinding">,
  ): Promise<void> {
    if (!assessmentId || !assetId) return;

    const records = await this.getRecords();
    const existingIndex = records.findIndex(
      (record) =>
        record.assessmentId === assessmentId && record.assetId === assetId,
    );

    const record: AssetCompletionRecord = {
      assessmentId,
      assetId,
      ...fields,
      completedAt: Date.now(),
    };

    if (existingIndex !== -1) {
      records[existingIndex] = record;
    } else {
      records.push(record);
    }

    await this.storageService.set(this.storageKey, records);
  }

  async removeRecord(assessmentId: string, assetId: string): Promise<void> {
    if (!assessmentId || !assetId) return;

    const records = await this.getRecords();
    const remaining = records.filter(
      (record) =>
        !(record.assessmentId === assessmentId && record.assetId === assetId),
    );

    if (remaining.length !== records.length) {
      await this.storageService.set(this.storageKey, remaining);
    }
  }

  async isCompleted(assessmentId: string, assetId: string): Promise<boolean> {
    if (!assessmentId || !assetId) return false;

    const records = await this.getRecords();
    return records.some(
      (record) =>
        record.assessmentId === assessmentId &&
        record.assetId === assetId &&
        record.isAssessmentCompleted,
    );
  }

  async isNoFinding(assessmentId: string, assetId: string): Promise<boolean> {
    if (!assessmentId || !assetId) return false;

    const records = await this.getRecords();
    return records.some(
      (record) =>
        record.assessmentId === assessmentId &&
        record.assetId === assetId &&
        record.noFinding === true,
    );
  }

  async getRecordsForAssessment(
    assessmentId: string | undefined,
  ): Promise<AssetCompletionRecord[]> {
    if (!assessmentId) return [];
    const records = await this.getRecords();
    return records.filter((record) => record.assessmentId === assessmentId);
  }
  async mergeOverrides(
    assessmentId: string | undefined,
    assets: AssessmentAssetModel[],
  ): Promise<AssessmentAssetModel[]> {
    const overrides = await this.getRecordsForAssessment(assessmentId);
    if (!overrides.length) return assets;

    const merged = assets.map((asset) => {
      const override = overrides.find((record) => record.assetId === asset.id);
      return override
        ? {
            ...asset,
            isAssessmentCompleted: override.isAssessmentCompleted,
            noFinding: override.noFinding ?? false,
          }
        : asset;
    });

    overrides.forEach((record) => {
      if (!merged.some((asset) => asset.id === record.assetId)) {
        merged.push({
          id: record.assetId,
          isAssessmentCompleted: record.isAssessmentCompleted,
          noFinding: record.noFinding ?? false,
          totalFindings: 0,
        });
      }
    });

    return merged;
  }

  private async getRecords(): Promise<AssetCompletionRecord[]> {
    const stored = (await this.storageService.get(this.storageKey)) as
      | AssetCompletionRecord[]
      | null;
    return Array.isArray(stored) ? stored : [];
  }
}
